import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { Clock, LogOut, Send, CheckCircle2 } from "lucide-react";

/** Full-screen block shown to admins whose subscription has expired.
 *  Replaces all admin routes — they can only send a reactivation request or log out. */
export default function AdminExpiredScreen() {
  const { user, logout } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const lastActivation = user?.subscription_expires_at;
  const lastActivationFmt = lastActivation ? formatDate(lastActivation) : "—";

  const requestActivation = async () => {
    setSubmitting(true);
    try {
      await api.post("/admin/request-activation");
      setSent(true);
      toast.success("Solicitud enviada al Owner");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setSent(true); // already requested recently
        toast.message(detail || "Ya enviaste una solicitud reciente");
      } else {
        toast.error(formatApiError(detail) || "No se pudo enviar la solicitud");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a0f2e] via-[#3e166c] to-[#7248ae] flex items-center justify-center p-4" data-testid="admin-expired-screen">
      <Card className="w-full max-w-lg p-8 sm:p-10 bg-white border-0 shadow-2xl">
        <div className="flex flex-col items-center text-center space-y-6">
          <Logo size={72} variant="plain" />

          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#e11d48]/10 border border-[#e11d48]/30">
            <Clock className="h-3.5 w-3.5 text-[#e11d48]" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[#e11d48]">Suscripción vencida</span>
          </div>

          <div className="space-y-2">
            <h1 className="font-display text-3xl sm:text-4xl text-[#1a0f2e]">Tu suscripción ha vencido</h1>
            <p className="text-[#7248ae] text-sm sm:text-base max-w-md mx-auto leading-relaxed">
              Para volver a tomar pedidos, gestionar tu menú e inventario,
              solicita la reactivación al administrador del sistema.
            </p>
          </div>

          <div className="w-full rounded-2xl bg-[#fcfbfd] border border-[#b182ec]/30 p-5 space-y-2 text-left">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#7248ae]">Hola, {user?.name}</p>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm text-[#1a0f2e]">Última fecha de activación:</span>
              <span className="font-display text-base text-[#3e166c]" data-testid="expired-last-activation">{lastActivationFmt}</span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm text-[#1a0f2e]">Restaurante:</span>
              <span className="text-sm text-[#3e166c] font-medium">{user?.email}</span>
            </div>
          </div>

          {sent ? (
            <div className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#10b981]/10 border border-[#10b981]/30 px-4 py-3 text-[#065f46]" data-testid="activation-sent-banner">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-medium">Solicitud enviada. El Owner ha sido notificado.</span>
            </div>
          ) : (
            <Button
              onClick={requestActivation}
              disabled={submitting}
              className="w-full bg-[#3e166c] hover:bg-[#1a0f2e] text-white py-6 text-base"
              data-testid="request-activation-button"
            >
              <Send className="h-4 w-4 mr-2" />
              {submitting ? "Enviando…" : "Solicitar activación"}
            </Button>
          )}

          <button
            onClick={logout}
            className="text-xs text-[#7248ae] hover:text-[#3e166c] inline-flex items-center gap-1 underline underline-offset-2"
            data-testid="expired-logout-button"
          >
            <LogOut className="h-3 w-3" />
            Cerrar sesión
          </button>
        </div>
      </Card>
    </div>
  );
}
